import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import ThemedChartTooltip, { ThemedTooltipBox } from '../../shared/ThemedChartTooltip';
import ThinScroll from '../../shared/ThinScroll';
import { SkeletonText, RefetchBar } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';
import InfoTip from '../../shared/InfoTip';
import { fmtCurrency, fmtNumber, fmtPercent, fmtRatio } from '../../shared/format';
import { BRAND_BLUE } from '../../shared/colors';
import {
  ExportMenu,
  campStatusBadge,
  campTypeBadge,
  downloadCsv,
  downloadPdf,
  makeExport,
  matchTypeBadge,
  parseAdAssets,
  prettyAdType,
  stripMatchSymbols,
  titleCaseLabel,
} from '../shared';
import Pagination from '../../shared/Pagination';
import { getGoogleAdsCampaignDrill } from '../../../api/auth';

// 25/page for consistency with the other GA tables. This drives the drill-down
// search-terms RPC `limit` (server-paginated via fn_get_gads_search_terms), so
// each page fetches 25 rows — still the Supabase RPC, no legacy API.
const DRILL_SEARCH_PER_PAGE = 25;

// Export spec — one column list drives CSV and PDF so they can't drift. CSV emits
// raw values (full-precision, unformatted) for downstream spreadsheets; PDF emits
// the formatted strings + abbreviated headers that fit the page width.
const CAMPAIGN_EXPORT_COLUMNS = [
  { header: 'Campaign', get: (campaign) => campaign.campaign_name },
  { header: 'Status', get: (campaign) => campaign.campaign_status },
  { header: 'Type', get: (campaign) => campaign.campaign_type },
  { header: 'Strategy', get: (campaign) => campaign.bid_strategy || '' },
  { header: 'Budget', get: (campaign) => campaign.daily_budget, pdf: (campaign) => fmtCurrency(campaign.daily_budget) },
  { header: 'Impressions', pdfHeader: 'Impr.', get: (campaign) => campaign.impressions, pdf: (campaign) => fmtNumber(campaign.impressions) },
  { header: 'Clicks', get: (campaign) => campaign.clicks, pdf: (campaign) => fmtNumber(campaign.clicks) },
  { header: 'CTR', get: (campaign) => (campaign.ctr * 100).toFixed(2) + '%', pdf: (campaign) => fmtPercent(campaign.ctr) },
  { header: 'Cost', get: (campaign) => campaign.cost, pdf: (campaign) => fmtCurrency(campaign.cost) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (campaign) => campaign.conversions, pdf: (campaign) => fmtNumber(campaign.conversions, true) },
  { header: 'Value', get: (campaign) => campaign.conversion_value, pdf: (campaign) => fmtCurrency(campaign.conversion_value) },
  { header: 'CPA', get: (campaign) => campaign.cost_per_conversion ?? '', pdf: (campaign) => (campaign.cost_per_conversion != null ? fmtCurrency(campaign.cost_per_conversion) : '—') },
  { header: 'ROAS', get: (campaign) => campaign.roas, pdf: (campaign) => fmtRatio(campaign.roas) },
];
const CAMPAIGN_EXPORT = makeExport(CAMPAIGN_EXPORT_COLUMNS);

/* ---------------- Drill tab: Overview ---------------- */
const OverviewTab = ({ ov, campaignName }) => {
  if (!ov) return <div className="text-xs text-gray-400 py-6 text-center">No overview data available for {campaignName} in this date range.</div>;
  const trend = ov.trend || [];
  const summary = ov.summary || {};
  const miniData = trend.map((datum) => ({
    ...datum,
    dateLabel: new Date(datum.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
  }));
  return (
    <div>
      <div className="rotate-x-labels bg-white dark:bg-gray-900 rounded-lg border dark:border-gray-700 p-4 mb-4">
        <div className="flex items-center gap-4 mb-3 text-[12px] text-gray-400">
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-[#32B1FF] inline-block rounded"></span> Cost</span>
          <span className="flex items-center gap-1"><span className="w-3 h-0.5 bg-emerald-400 inline-block rounded"></span> Conversions</span>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <ComposedChart data={miniData} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
            <XAxis dataKey="dateLabel" tick={{ fontSize: 9, fill: '#9ca3af' }} interval={miniData.length > 14 ? Math.floor(miniData.length / 5) - 1 : 0} axisLine={{ stroke: '#374151' }} tickLine={false} />
            <YAxis yAxisId="left" tick={{ fontSize: 9, fill: '#3B82F6' }} tickFormatter={(value) => `$${value}`} axisLine={false} tickLine={false} width={40} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9, fill: '#10B981' }} allowDecimals={false} axisLine={false} tickLine={false} width={25} />
            <ThemedChartTooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const dd = payload[0].payload;
                return (
                  <ThemedTooltipBox title={label}>
                    <div>Cost: {fmtCurrency(dd.cost)}</div>
                    {/* Review #4: trend rows carry `all_conversions`, not
                        `conversions` — the old key rendered "Conv: —". */}
                    <div>Conv: {fmtNumber(dd.all_conversions ?? dd.conversions, true)}</div>
                  </ThemedTooltipBox>
                );
              }}
            />
            <Line yAxisId="left" type="monotone" dataKey="cost" stroke={BRAND_BLUE} strokeWidth={2} dot={miniData.length <= 14} activeDot={{ r: 3 }} />
            {/* Review #4: the trend series key is `all_conversions` (the RPC
                doesn't emit `conversions` per-day) — was rendering a flat line. */}
            <Line yAxisId="right" type="monotone" dataKey="all_conversions" stroke="#34d399" strokeWidth={2} dot={false} strokeDasharray="4 2" />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 max-[344px]:grid-cols-1">
        {[
          { label: 'Total Spend', value: fmtCurrency(summary.total_spend) },
          // Review #4 (2026-07-23): fn_get_gads_campaign_overview.summary exposes
          // conversions under `conversions` / `conversion_value` (no `total_`
          // prefix). The old keys were always undefined → rendered "—" while
          // Spend/CPA/ROAS worked. Fall back to the total_ keys in case a future
          // RPC revision reintroduces them.
          { label: 'Conversions', value: fmtNumber(summary.conversions ?? summary.total_conversions, true) },
          { label: 'Conv. Value', value: fmtCurrency(summary.conversion_value ?? summary.total_conversion_value) },
          { label: 'CPA', value: fmtCurrency(summary.cpa) },
          { label: 'ROAS', value: fmtRatio(summary.roas) },
          { label: 'Avg Daily Spend', value: fmtCurrency(summary.avg_daily_spend) },
        ].map((stat) => (
          <div key={stat.label} className="bg-white dark:bg-gray-900 rounded-lg border dark:border-gray-700 px-3 py-2.5 text-center">
            <div className="text-[12px] text-gray-400 uppercase tracking-wide">{stat.label}</div>
            <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{stat.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ---------------- Drill tab: Search Terms ---------------- */
const SearchTermsTab = ({ st, campaignName, drillSearchPage, onPageChange, loading }) => {
  if (!st?.items?.length) return <div className="text-xs text-gray-400 py-6 text-center">No search term data available for {campaignName} in this date range.</div>;
  const totalItems = st.pagination?.total_items || 0;
  const totalPages = st.pagination?.total_pages || 1;
  const cp = drillSearchPage;
  const perPage = DRILL_SEARCH_PER_PAGE;

  return (
    <div className="relative">
      {loading && <RefetchBar />}
      <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
      <ThinScroll className="responsive-card-table-650-wrapper overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
        <table className="responsive-card-table-650 w-full text-xs min-w-[900px]" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '28%' }} /><col style={{ width: '16%' }} /><col style={{ width: '12%' }} />
            <col style={{ width: '9%' }} /><col style={{ width: '9%' }} /><col style={{ width: '10%' }} />
            <col style={{ width: '8%' }} /><col style={{ width: '8%' }} />
          </colgroup>
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <th className="px-3 py-2 text-left text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Search Term</th>
              <th className="px-3 py-2 text-left text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Ad Group</th>
              <th className="px-3 py-2 text-center text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Match</th>
              <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Impr.</th>
              <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Clicks</th>
              <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Cost</th>
              <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Conv.</th>
              <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">CTR</th>
            </tr>
          </thead>
          <tbody>
            {/* Keyed by the row's own identity, not its array index: these rows are
                SERVER-paged, so index 0 is a different search term on every page and
                an index key made React reuse the previous page's DOM node (stale
                focus/selection). The same term can recur across ad groups and match
                types, so all three fields are needed for uniqueness. */}
            {st.items.map((row) => (
              <tr key={`${row.search_term}|${row.adgroup_name}|${row.match_type}`} className="border-b dark:border-gray-800 last:border-0">
                <td data-label="Search Term" className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium">{row.search_term}</td>
                <td data-label="Ad Group" className="px-3 py-2 text-gray-500 dark:text-gray-400">{row.adgroup_name}</td>
                <td data-label="Match" className="px-3 py-2 text-center">{matchTypeBadge(row.match_type)}</td>
                <td data-label="Impr." className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.impressions)}</td>
                <td data-label="Clicks" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.clicks)}</td>
                <td data-label="Cost" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtCurrency(row.cost)}</td>
                <td data-label="Conv." className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.conversions, true)}</td>
                <td data-label="CTR" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtPercent(row.ctr)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ThinScroll>
      <Pagination
        page={cp}
        totalPages={totalPages}
        onPageChange={onPageChange}
        totalItems={totalItems}
        perPage={perPage}
        label="terms"
      />
      </div>
    </div>
  );
};

/* ---------------- Drill tab: Keywords ---------------- */
const KeywordsTab = ({ kw, campaignName }) => {
  if (!kw?.items?.length) return <div className="text-xs text-gray-400 py-6 text-center">No keyword data available for {campaignName} in this date range.</div>;
  return (
    <ThinScroll className="responsive-card-table-650-wrapper overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      <table className="responsive-card-table-650 w-full text-xs min-w-[1000px]">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-3 py-2 text-left text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Keyword</th>
            <th className="px-3 py-2 text-center text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Match</th>
            <th className="px-3 py-2 text-left text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Ad Group</th>
            <th className="px-3 py-2 text-center text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Status</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Impr.</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Clicks</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Cost</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Conv.</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">CPA</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">CTR</th>
          </tr>
        </thead>
        <tbody>
          {kw.items.map((row, index) => (
            <tr key={index} className="border-b dark:border-gray-800">
              <td data-label="Keyword" className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium">{stripMatchSymbols(row.keyword_text) || '—'}</td>
              <td data-label="Match" className="px-3 py-2 text-center">{matchTypeBadge(row.match_type)}</td>
              <td data-label="Ad Group" className="px-3 py-2 text-gray-500 dark:text-gray-400">{row.adgroup_name}</td>
              <td data-label="Status" className="px-3 py-2 text-center">{campStatusBadge(row.keyword_status)}</td>
              <td data-label="Impr." className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.impressions)}</td>
              <td data-label="Clicks" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.clicks)}</td>
              <td data-label="Cost" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtCurrency(row.cost)}</td>
              <td data-label="Conv." className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.conversions, true)}</td>
              <td data-label="CPA" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{row.cost_per_conversion ? fmtCurrency(row.cost_per_conversion) : <span className="text-gray-400">—</span>}</td>
              <td data-label="CTR" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtPercent(row.ctr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ThinScroll>
  );
};

/* ---------------- Drill tab: Ad Groups ---------------- */
const AdGroupsTab = ({ ag, campaignName }) => {
  if (!ag?.items?.length) return <div className="text-xs text-gray-400 py-6 text-center">No ad group data available for {campaignName} in this date range.</div>;
  return (
    <ThinScroll className="responsive-card-table-650-wrapper overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      <table className="responsive-card-table-650 w-full text-xs min-w-[900px]">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-3 py-2 text-left text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Ad Group</th>
            <th className="px-3 py-2 text-center text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Status</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Impr.</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Clicks</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Cost</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Conv.</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Conv. Value</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">CPA</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">CTR</th>
          </tr>
        </thead>
        <tbody>
          {ag.items.map((row, index) => (
            <tr key={index} className="border-b dark:border-gray-800">
              <td data-label="Ad Group" className="px-3 py-2 text-gray-900 dark:text-gray-100 font-medium">{row.adgroup_name}</td>
              <td data-label="Status" className="px-3 py-2 text-center">{campStatusBadge(row.adgroup_status)}</td>
              <td data-label="Impr." className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.impressions)}</td>
              <td data-label="Clicks" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.clicks)}</td>
              <td data-label="Cost" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtCurrency(row.cost)}</td>
              <td data-label="Conv." className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.conversions, true)}</td>
              <td data-label="Conv. Value" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtCurrency(row.conversion_value)}</td>
              <td data-label="CPA" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{row.cost_per_conversion ? fmtCurrency(row.cost_per_conversion) : <span className="text-gray-400">—</span>}</td>
              <td data-label="CTR" className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtPercent(row.ctr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ThinScroll>
  );
};

/* ---------------- Drill tab: Ads ---------------- */
// Ad Title cell: RSAs pack every headline into a pipe-delimited `ad_title`.
// Dumping all 15 into a table cell makes rows unreadable, so show the ad group +
// a "N headlines" chip that expands the verbatim list inline. Expanded Text Ads
// arrive as a bare "Ad <id>" (no asset list) — show just the ad group (the Type
// column already labels them "Expanded Text Ad").
const AdTitleCell = ({ row }) => {
  const [open, setOpen] = useState(false);
  const assets = parseAdAssets(row.ad_title);
  const label = titleCaseLabel(row.adgroup_name) || '—';

  if (!assets.length) {
    return <span className="font-medium text-gray-900 dark:text-gray-100">{label}</span>;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-gray-900 dark:text-gray-100">{label}</span>
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium text-[#32B1FF] hover:bg-[#32B1FF]/10 transition-colors"
        >
          {assets.length} headline{assets.length === 1 ? '' : 's'}
          <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {open && (
        <p className="mt-1.5 text-[12px] leading-relaxed text-gray-500 dark:text-gray-400">
          {assets.map((asset, index) => (
            <React.Fragment key={`${asset}-${index}`}>
              {index > 0 && <span className="mx-1.5 text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>}
              {asset}
            </React.Fragment>
          ))}
        </p>
      )}
    </div>
  );
};

const AdsTab = ({ adData, campaignName }) => {
  if (!adData?.items?.length) return <div className="text-xs text-gray-400 py-6 text-center">No ad data available for {campaignName} in this date range.</div>;
  return (
    <ThinScroll className="responsive-card-table-650-wrapper overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
      <table className="responsive-card-table-650 w-full text-xs min-w-[1000px]">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <th className="px-3 py-2 text-left text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Ad</th>
            <th className="px-3 py-2 text-center text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Type</th>
            <th className="px-3 py-2 text-center text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Status</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Impr.</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Clicks</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Cost</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">Conv.</th>
            <th className="px-3 py-2 text-right text-[12px] font-semibold uppercase text-gray-500 dark:text-gray-400">CTR</th>
          </tr>
        </thead>
        <tbody>
          {adData.items.map((row) => (
            <tr key={row.ad_id} className="border-b dark:border-gray-800">
              <td data-label="Ad" className="px-3 py-2 align-top">
                <AdTitleCell row={row} />
              </td>
              <td data-label="Type" className="px-3 py-2 text-center align-top">
                <span className="inline-block px-2 py-0.5 rounded text-[11px] font-semibold bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 whitespace-nowrap">
                  {prettyAdType(row.ad_type)}
                </span>
              </td>
              <td data-label="Status" className="px-3 py-2 text-center align-top">{campStatusBadge(row.ad_status)}</td>
              <td data-label="Impr." className="px-3 py-2 text-right align-top text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.impressions)}</td>
              <td data-label="Clicks" className="px-3 py-2 text-right align-top text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.clicks)}</td>
              <td data-label="Cost" className="px-3 py-2 text-right align-top text-gray-700 dark:text-gray-300 tabular-nums">{fmtCurrency(row.cost)}</td>
              <td data-label="Conv." className="px-3 py-2 text-right align-top text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(row.conversions, true)}</td>
              <td data-label="CTR" className="px-3 py-2 text-right align-top text-gray-700 dark:text-gray-300 tabular-nums">{fmtPercent(row.ctr)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </ThinScroll>
  );
};

/* ---------------- Drill tab: Settings ---------------- */
const SettingsTab = ({ cfg, campaignName }) => {
  const [showAll, setShowAll] = useState(false);
  // Review #5: the API layer now unwraps the settings array to a single row,
  // but guard defensively in case an array still arrives.
  const row = Array.isArray(cfg) ? cfg.find((r) => r?.campaign_name === campaignName) || cfg[0] : cfg;
  if (!row) return <div className="text-xs text-gray-400 py-6 text-center">No settings data available for {campaignName}.</div>;
  const fields = [
    { label: 'Campaign Type', value: row.campaign_type },
    // Review #5: don't hardcode "Paused" when status is missing — that misreports
    // an ENABLED campaign. Map ENABLED/PAUSED explicitly; anything else → "—".
    { label: 'Status', value: row.status === 'ENABLED' ? 'Active' : row.status === 'PAUSED' ? 'Paused' : '—' },
    { label: 'Bid Strategy', value: (row.bid_strategy || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase()) },
    { label: 'Daily Budget', value: `${fmtCurrency(row.daily_budget)}/day` },
    ...(row.target_cpa != null ? [{ label: 'Target CPA', value: fmtCurrency(row.target_cpa) }] : []),
    ...(row.target_roas != null ? [{ label: 'Target ROAS', value: fmtRatio(row.target_roas) }] : []),
    { label: 'Geo Targeting', value: row.geo_target_type },
    { label: 'Start Date', value: row.start_date },
    { label: 'End Date', value: row.end_date },
    // Review #5: fn_get_gads_campaign_settings does not expose a last-updated
    // timestamp (no extracted_at/updated_at in the row) — stays "—" until the
    // RPC adds one. Flagged for backend.
    { label: 'Last Updated', value: row.extracted_at ? new Date(row.extracted_at).toLocaleString() : '—' },
  ];
  // Below 500px only the first few key settings show; the rest sit behind a toggle.
  const KEY_COUNT = 3;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-[539px]:grid-cols-1">
      {fields.map((field, index) => (
        <div
          key={field.label}
          className={`bg-white dark:bg-gray-900 rounded-lg border dark:border-gray-700 px-4 py-3 ${index >= KEY_COUNT && !showAll ? 'max-[499px]:hidden' : ''}`}
        >
          <div className="text-[12px] text-gray-400 uppercase tracking-wide">{field.label}</div>
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">{field.value}</div>
        </div>
      ))}
      {fields.length > KEY_COUNT && (
        <button
          type="button"
          onClick={() => setShowAll((prev) => !prev)}
          className="hidden max-[499px]:flex items-center justify-center gap-1 py-2 text-xs font-medium text-[#32B1FF] rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
        >
          {showAll ? 'Show less' : 'Show more'}
          <svg className={`w-3 h-3 transition-transform ${showAll ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
    </div>
  );
};

/* ---------------- Main table + row drilldown ---------------- */
const CAMP_COLUMNS = [
  { key: 'campaign_name', label: 'Campaign', align: 'left', minW: 'min-w-[180px]' },
  { key: 'campaign_status', label: 'Status', align: 'center', minW: '' },
  { key: 'campaign_type', label: 'Type', align: 'center', minW: '' },
  { key: 'bid_strategy', label: 'Strategy', align: 'left', minW: '' },
  { key: 'daily_budget', label: 'Budget', align: 'right', minW: '' },
  { key: 'impressions', label: 'Impr.', align: 'right', minW: '' },
  { key: 'clicks', label: 'Clicks', align: 'right', minW: '' },
  { key: 'ctr', label: 'CTR', align: 'right', minW: '' },
  { key: 'cost', label: 'Cost', align: 'right', minW: '' },
  { key: 'conversions', label: 'Conv.', align: 'right', minW: '' },
  { key: 'conversion_value', label: 'Value', align: 'right', minW: '' },
  { key: 'cost_per_conversion', label: 'CPA', align: 'right', minW: '' },
  { key: 'roas', label: 'ROAS', align: 'right', minW: '' },
];

const CampaignTable = ({
  campaigns, loading, error,
  sortBy, sortDir, onSort,
  page, perPage, onPageChange,
  selectedClientId, rangeStart, rangeEnd,
}) => {
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [drillTab, setDrillTab] = useState('overview');
  const [drillData, setDrillData] = useState({});
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearchPage, setDrillSearchPage] = useState(1);

  // Stale-response guard: drill loads are user-driven (expanding a row, switching
  // tab, paging search terms), so a slow earlier request can land after a newer
  // one and show the wrong tab's rows. Only the newest request may commit.
  const drillReqRef = useRef(0);

  const loadDrillTab = useCallback(async (campaignName, tab, drillPage = 1) => {
    const myReq = ++drillReqRef.current;
    setDrillLoading(true);
    try {
      const extraParams = tab === 'searchTerms' ? { page: drillPage, limit: DRILL_SEARCH_PER_PAGE } : {};
      const resp = await getGoogleAdsCampaignDrill(selectedClientId, campaignName, tab, rangeStart, rangeEnd, extraParams);
      if (myReq !== drillReqRef.current) return;
      setDrillData((prev) => ({ ...prev, [tab]: resp?.data ?? null }));
    } catch {
      if (myReq !== drillReqRef.current) return;
      setDrillData((prev) => ({ ...prev, [tab]: null }));
    } finally {
      if (myReq === drillReqRef.current) setDrillLoading(false);
    }
  }, [selectedClientId, rangeStart, rangeEnd]);

  // Reset drill state when filters change
  useEffect(() => {
    setExpandedCampaign(null);
    setDrillData({});
    setDrillTab('overview');
    setDrillSearchPage(1);
  }, [selectedClientId, rangeStart, rangeEnd]);

  const handleCampaignRowClick = (campaignName) => {
    if (expandedCampaign === campaignName) {
      setExpandedCampaign(null);
      setDrillData({});
      setDrillTab('overview');
      setDrillSearchPage(1);
    } else {
      setExpandedCampaign(campaignName);
      setDrillTab('overview');
      setDrillData({});
      setDrillSearchPage(1);
      loadDrillTab(campaignName, 'overview');
    }
  };

  const handleDrillTabChange = (tab) => {
    setDrillTab(tab);
    setDrillSearchPage(1);
    if (!drillData[tab]) {
      loadDrillTab(expandedCampaign, tab);
    }
  };

  const handleDrillSearchPageChange = (pageNumber) => {
    setDrillSearchPage(pageNumber);
    loadDrillTab(expandedCampaign, 'searchTerms', pageNumber);
  };

  const exportCsv = () => {
    if (!campaigns?.items?.length) return;
    downloadCsv(CAMPAIGN_EXPORT.headers, campaigns.items.map(CAMPAIGN_EXPORT.toCsvRow), `campaigns-${rangeStart}-to-${rangeEnd}.csv`);
  };
  const exportPdf = () => {
    if (!campaigns?.items?.length) return;
    downloadPdf('Campaign Performance', CAMPAIGN_EXPORT.pdfHeaders, campaigns.items.map(CAMPAIGN_EXPORT.toPdfRow), `campaigns-${rangeStart}-to-${rangeEnd}.pdf`, `${rangeStart} to ${rangeEnd}`);
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800">
      <div className="p-4 sm:p-5 border-b dark:border-gray-800 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 inline-flex items-center">
            Campaign Performance
            <InfoTip text="Per-campaign performance — spend, clicks, conversions, and ROAS. Click any campaign to drill into its keywords, ads, and search terms." />
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {campaigns?.pagination?.total_items ?? 0} campaign{(campaigns?.pagination?.total_items ?? 0) !== 1 ? 's' : ''} · sorted by {sortBy.replace(/_/g, ' ')} {sortDir === 'desc' ? '↓' : '↑'}
          </p>
        </div>
        {campaigns?.items?.length > 0 && <ExportMenu onCsv={exportCsv} onPdf={exportPdf} />}
      </div>

      <div className="p-4 sm:p-5 relative">
        {/* First load: skeleton. Page-change / sort refetch: keep prior rows
            visible (dimmed) under a slim progress bar so the user keeps their
            place — same pattern as CI's Demand Signals panel. */}
        {error ? (
          <ErrorState message={error} />
        ) : loading && !campaigns ? (
          <SkeletonText lines={10} />
        ) : !campaigns?.items?.length ? (
          <div className="text-center py-12 text-sm text-gray-400 dark:text-gray-500">
            No campaign data found for this date range. Google Ads data is extracted weekly — check that this client has an active Google Ads account.
          </div>
        ) : (
          <>
            {loading && <RefetchBar />}
            <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
            <ThinScroll className="responsive-campaign-table-wrapper overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
              <table className="responsive-campaign-table w-full text-xs min-w-[1100px]">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                    {/* Sort control is a real <button> inside the <th> (keyboard-reachable),
                        with aria-sort on the th to announce the active direction. */}
                    {CAMP_COLUMNS.map((col) => (
                      <th
                        key={col.key}
                        scope="col"
                        aria-sort={sortBy === col.key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                        className={`px-3 py-3 text-[12px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 whitespace-nowrap ${col.minW} ${
                          col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => onSort(col.key)}
                          title={`Sort by ${col.label}`}
                          className={`inline-flex items-center gap-1 select-none uppercase tracking-wider font-semibold hover:text-gray-900 dark:hover:text-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF]/40 rounded-sm ${col.align === 'right' ? 'justify-end w-full' : col.align === 'center' ? 'justify-center w-full' : ''}`}
                        >
                          {col.label}
                          {sortBy === col.key && (
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" d={sortDir === 'asc' ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'} />
                            </svg>
                          )}
                        </button>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {campaigns.items.map((campaign) => {
                    const isExpanded = expandedCampaign === campaign.campaign_name;
                    return (
                      <React.Fragment key={campaign.campaign_id}>
                        <tr
                          onClick={() => handleCampaignRowClick(campaign.campaign_name)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleCampaignRowClick(campaign.campaign_name);
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for ${campaign.campaign_name}`}
                          className={`camp-row hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#32B1FF] focus-visible:ring-inset ${isExpanded ? 'bg-gray-50 dark:bg-gray-800/60' : 'camp-collapsed border-b dark:border-gray-800'}`}
                        >
                          <td className="camp-name-cell px-3 py-3.5 font-medium text-[#32B1FF] whitespace-nowrap">
                            <span className="inline-flex items-center gap-1.5">
                              <svg className={`w-3 h-3 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                              </svg>
                              <span className="hidden max-[649px]:inline text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Campaign</span>
                            </span>
                            <span className="ml-1.5 max-[649px]:ml-0">{campaign.campaign_name}</span>
                          </td>
                          <td data-label="Status" className="px-3 py-3.5 text-center">{campStatusBadge(campaign.campaign_status)}</td>
                          <td data-label="Type" className="px-3 py-3.5 text-center">{campTypeBadge(campaign.campaign_type)}</td>
                          <td data-label="Strategy" className="px-3 py-3.5 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                            {(campaign.bid_strategy || '—').replace('MAXIMIZE_', 'Max ').replace('TARGET_', 'Target ').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase())}
                          </td>
                          <td data-label="Budget" className="px-3 py-3.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap"><span>{fmtCurrency(campaign.daily_budget)}<span className="text-gray-400 dark:text-gray-500">/day</span></span></td>
                          <td data-label="Impr." className="px-3 py-3.5 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(campaign.impressions)}</td>
                          <td data-label="Clicks" className="px-3 py-3.5 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(campaign.clicks)}</td>
                          <td data-label="CTR" className="px-3 py-3.5 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtPercent(campaign.ctr)}</td>
                          <td data-label="Cost" className="px-3 py-3.5 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtCurrency(campaign.cost)}</td>
                          <td data-label="Conv." className="px-3 py-3.5 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtNumber(campaign.conversions, true)}</td>
                          <td data-label="Value" className="px-3 py-3.5 text-right text-gray-700 dark:text-gray-300 tabular-nums">{fmtCurrency(campaign.conversion_value)}</td>
                          <td data-label="CPA" className="px-3 py-3.5 text-right text-gray-700 dark:text-gray-300 tabular-nums">{campaign.cost_per_conversion != null ? fmtCurrency(campaign.cost_per_conversion) : <span className="text-gray-400">—</span>}</td>
                          <td data-label="ROAS" className="px-3 py-3.5 text-right font-semibold text-gray-900 dark:text-gray-100 tabular-nums">{fmtRatio(campaign.roas)}</td>
                        </tr>

                        {isExpanded && (
                          <tr className="camp-drill-row">
                            <td colSpan={13} className="p-0">
                              <div className="bg-gray-50 dark:bg-gray-800/40 border-b dark:border-gray-700 px-6 py-5">
                                <div className="flex flex-wrap gap-1 mb-4 border-b dark:border-gray-700 pb-3 max-[509px]:grid max-[509px]:grid-cols-3 max-[459px]:grid-cols-2">
                                  {[
                                    { key: 'overview', label: 'Overview' },
                                    { key: 'searchTerms', label: 'Search Terms' },
                                    { key: 'keywords', label: 'Keywords' },
                                    { key: 'adGroups', label: 'Ad Groups' },
                                    { key: 'ads', label: 'Ads' },
                                    { key: 'settings', label: 'Settings' },
                                  ].map((tab) => (
                                    <button
                                      key={tab.key}
                                      type="button"
                                      onClick={(event) => { event.stopPropagation(); handleDrillTabChange(tab.key); }}
                                      className={`px-4 py-2 text-xs font-medium rounded-lg transition-all ${
                                        drillTab === tab.key
                                          ? 'bg-[#32B1FF] text-white shadow-sm'
                                          : 'text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                                      }`}
                                    >
                                      {tab.label}
                                    </button>
                                  ))}
                                </div>

                                {drillLoading && !drillData[drillTab] ? (
                                  <div className="py-4"><SkeletonText lines={6} /></div>
                                ) : (
                                  <>
                                    {drillTab === 'overview' && <OverviewTab ov={drillData.overview} campaignName={campaign.campaign_name} />}
                                    {drillTab === 'searchTerms' && (
                                      <SearchTermsTab
                                        st={drillData.searchTerms}
                                        campaignName={campaign.campaign_name}
                                        drillSearchPage={drillSearchPage}
                                        onPageChange={handleDrillSearchPageChange}
                                        loading={drillLoading}
                                      />
                                    )}
                                    {drillTab === 'keywords' && <KeywordsTab kw={drillData.keywords} campaignName={campaign.campaign_name} />}
                                    {drillTab === 'adGroups' && <AdGroupsTab ag={drillData.adGroups} campaignName={campaign.campaign_name} />}
                                    {drillTab === 'ads' && <AdsTab adData={drillData.ads} campaignName={campaign.campaign_name} />}
                                    {drillTab === 'settings' && <SettingsTab cfg={drillData.settings} campaignName={campaign.campaign_name} />}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </ThinScroll>

            <Pagination
              page={page}
              totalPages={campaigns.pagination?.total_pages ?? 1}
              onPageChange={onPageChange}
              totalItems={campaigns.pagination?.total_items ?? 0}
              perPage={perPage}
              label="campaigns"
            />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CampaignTable;
